<template>
    <on-window-resize :execute="onWindowResize">
        <div>
            <div
                class="pb-24 text-white xs:pb-32 md:pb-48 xl:pb-64 bg-blue"
                :style="{
                    '-webkit-clip-path': `polygon(0 0, 100% 0, 100% ${angle}%, 0 100%)`,
                    'clip-path': `polygon(0 0, 100% 0, 100% ${angle}%, 0 100%)`
                }"
            >
                <div class="container max-w-xl mx-auto">
                    <div class="max-w-lg py-8 mx-auto lg:mb-6 md:text-center">
                        <router-link
                            :to="{ name: 'www.home.index' }"
                            class="inline-block h-5 mb-5 md:mb-6 xl:mb-10"
                        >
                            <base-svg
                                name="logo-rentora"
                                class="h-full fill-primary-white"
                            />
                        </router-link>

                        <h1 class="mb-4 text-4xl font-semibold leading-tight tracking-tight font-noto-sans xs:text-5xl lg:text-6xl">
                            List your property with Rentora
                        </h1>

                        <h2 class="mb-8 text-2xl md:text-3xl lg:text-4xl md:mb-12">
                            Free property listings with built in applications
                        </h2>

                        <remarketings-store
                            v-slot="{
                                sid,
                                is_loading: is_saving,
                                has_loaded: has_saved,
                                error,
                                store
                            }"
                            @success="onRemarketingsStoreSuccess"
                        >
                            <base-form
                                @submit="store(form)"
                                class="flex flex-col items-start max-w-sm mx-auto mb-3 md:flex-row"
                            >
                                <form-item
                                    :sid="sid"
                                    name="email"
                                    class="mb-3! md:mb-0! md:mr-3 w-full md:w-auto"
                                    error-class="text-white! text-left"
                                >
                                    <input-text
                                        v-model="form.email"
                                        name="email"
                                        type="email"
                                        placeholder="you@email.co.uk"
                                        class="shadow h-14 md:text-lg md:px-6"
                                    />
                                </form-item>

                                <base-button
                                    :is-loading="is_saving || has_saved"
                                    :disabled="is_saving || has_saved"
                                    class="w-full shadow h-14 md:text-lg md:w-48 btn btn-blue-darkest md:pt-4"
                                >
                                    Get Started
                                </base-button>
                            </base-form>
                        </remarketings-store>

                        <p class="max-w-xs mx-auto text-xs text-center md:max-w-sm">
                            Rentora listings are <strong>free</strong> - no setup, recurring or hidden fees. By entering your email, you agree to receive marketing emails from Rentora.
                        </p>
                    </div>
                </div>
            </div>

            <div class="container relative z-10 max-w-sm mx-auto -mt-24 lg:max-w-md xl:max-w-lg xs:-mt-32 md:-mt-48 xl:-mt-64">
                <img
                    src="/img/devices-listing.png"
                    class="w-full"
                >
            </div>

            <div class="container max-w-xl mx-auto">
                <grid-layout class="mt-6 mb-12 md:mt-12 lg:mb-24">
                    <grid-layout-item class="mb-8 md:mb-0 md:w-1/3">
                        <div class="inline-block p-2 mb-1 rounded-full sm:p-3 bg-blue-lightest">
                            <base-svg
                                name="icon-listing"
                                class="w-8 h-8 fill-secondary-blue fill-primary-blue-lighter"
                            />
                        </div>

                        <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                            Beautiful listings
                        </h6>

                        <p>
                            Show off your properties with a <strong>beautiful, mobile friendly listing</strong> - showcasing a gallery of <strong>photos, price, features and description</strong>.
                        </p>
                    </grid-layout-item>

                    <grid-layout-item class="mb-8 md:mb-0 md:w-1/3">
                        <div class="inline-block p-2 mb-1 rounded-full sm:p-3 bg-blue-lightest">
                            <base-svg
                                name="icon-inbox"
                                class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                            />
                        </div>

                        <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                            Applications, built in
                        </h6>

                        <p>
                            Every Rentora listing comes with a <strong>built in application</strong>. You’ll get all the information you need to <strong>choose the perfect tenant</strong>.
                        </p>
                    </grid-layout-item>

                    <grid-layout-item class="md:w-1/3">
                        <div class="inline-block p-2 mb-1 rounded-full sm:p-3 bg-blue-lightest">
                            <base-svg
                                name="icon-facebook-circle"
                                class="w-8 h-8 fill-secondary-blue-lightest fill-primary-blue"
                            />
                        </div>

                        <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                            Social media marketing
                        </h6>

                        <p>
                            <strong>Record numbers</strong> of properties are being marketed via Facebook posts. It’s time to join in. Our listings are <strong>built for social media</strong>.
                        </p>
                    </grid-layout-item>
                </grid-layout>

                <div class="mb-8 border-t border-b">
                    <h6 class="mt-10 mb-2 text-lg font-semibold text-center font-noto-sans text-grey-darkest">
                        Every month<sup class="text-xs">*</sup> our listings receive
                    </h6>

                    <div class="flex max-w-md mx-auto my-6">
                        <div class="flex-grow w-auto text-center">
                            <div class="text-xl font-semibold xs:text-2xl md:text-4xl lg:text-5xl font-noto-sans text-grey-darkest">
                                2,500
                            </div>
                            <div class="text-xs text-label lg:text-base">Searches</div>
                        </div>
                        <div class="flex-grow w-auto text-center">
                            <div class="text-xl font-semibold xs:text-2xl md:text-4xl lg:text-5xl font-noto-sans text-grey-darkest">
                                430
                            </div>
                            <div class="text-xs text-label lg:text-base">Views</div>
                        </div>
                        <div class="flex-grow w-auto text-center">
                            <div class="text-xl font-semibold xs:text-2xl lg:text-5xl md:text-4xl font-noto-sans text-grey-darkest">
                                12
                            </div>
                            <div class="text-xs text-label lg:text-base">Applications</div>
                        </div>
                    </div>

                    <div class="mb-3 text-xs text-right text-grey">
                        * on average
                    </div>
                </div>

                <blockquote class="max-w-md px-6 mx-auto mb-12 text-center">
                    <div class="mb-3 italic text-grey-darkest md:text-xl font-noto-sans">
                        <p>“I created a listing on Rentora and shared it on a local Facebook group. Within 24 hours I had about 15 applications!</p>
                    </div>

                    <cite class="not-italic text-label">
                        John W, Private Landlord
                    </cite>
                </blockquote>

                <div class="mb-12 text-center">
                    <remarketings-store
                        v-slot="{
                            sid,
                            is_loading: is_saving,
                            has_loaded: has_saved,
                            error,
                            store
                        }"
                        @success="onRemarketingsStoreSuccess"
                    >
                        <base-form
                            @submit="store(form)"
                            class="flex flex-col items-start max-w-sm mx-auto mb-3 md:flex-row"
                        >
                            <form-item
                                :sid="sid"
                                name="email"
                                class="mb-3! md:mb-0! md:mr-3 w-full md:w-auto"
                                error-class="text-left"
                            >
                                <input-text
                                    v-model="form.email"
                                    name="email"
                                    type="email"
                                    placeholder="you@email.co.uk"
                                    class="h-14 md:text-lg md:px-6"
                                />
                            </form-item>

                            <base-button
                                :is-loading="is_saving || has_saved"
                                :disabled="is_saving || has_saved"
                                class="w-full h-14 md:text-lg md:w-48 btn btn-blue md:pt-4"
                            >
                                Get Started
                            </base-button>
                        </base-form>
                    </remarketings-store>
                </div>

                <div class="container max-w-xl mx-auto mb-6">
                    <div class="flex flex-col items-center justify-center text-xs">
                        <div class="flex-none px-3">
                            <router-link
                                :to="{ name: 'www.policies.terms.index' }"
                                class="underline"
                            >
                                Terms,
                            </router-link>

                            <router-link
                                :to="{ name: 'www.policies.privacy.index' }"
                                class="underline"
                            >
                                Privacy
                            </router-link>

                            &amp;

                            <router-link
                                :to="{ name: 'www.policies.cookies.index' }"
                                class="underline"
                            >
                                Cookie
                            </router-link>

                            policies
                        </div>

                        <div class="flex-none px-3">
                            &copy; {{ $moment().format('Y') }} Tenant Manager Ltd.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </on-window-resize>
</template>

<script>
    import _ from 'lodash'
    import local from '../../../store/local'
    import HeadMixin from '../../../mixins/head'
    import BaseButton from '../../../components/base-button'
    import RemarketingsStore from '../../../components/remarketings-store'

    export default {
        mixins: [
            HeadMixin
        ],
        components: {
            BaseButton,
            RemarketingsStore
        },
        data () {
            return {
                angle: 100,
                form: {
                    email: '',
                    campaign: 'guest-free-listings'
                }
            }
        },
        watch: {
            'form.email' () {
                local.set('guest-email', this.form.email)
            }
        },
        created () {
            if (!this.form.email) {
                this.form.email = local.get('guest-email')
            }
        },
        methods: {
            head () {
                return {
                    title: [
                        'Market your properties for free',
                        'Listings for private landlords and estate agents'
                    ],
                    description: 'Publicly list your properties, and/or invite tenants to apply with a unique link. Every Rentora listing has a built-in rental application.',
                    meta: [
                        {
                            property: 'og:image',
                            content: _.url('/img/head/og-property-listings.png')
                        },
                        {
                            name: 'twitter:image',
                            content: _.url('/img/head/twitter-property-listings.png')
                        }
                    ]
                }
            },
            onWindowResize () {
                switch (true) {
                    case this.$screen.is('lg'):
                        this.angle = 85
                        break
                    case this.$screen.is('md'):
                        this.angle = 90
                        break
                    case this.$screen.is('xs'):
                        this.angle = 94
                        break
                    default:
                        this.angle = 96
                }
            },
            onRemarketingsStoreSuccess () {
                this.$track(
                    'lp.market-your-properties-for-free.index-4',
                    'form-submission',
                    'A guest is being remarketed to'
                )

                setTimeout(() => {
                    window.location = this.$laroute.route('auth.users.create', {
                        intended: this.$laroute.route('dashboard.landlord.listings.create', {
                            welcome: '✓'
                        })
                    })
                }, 0)
            }
        }
    }
</script>
